import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import NewsCard from "@/components/base/NewsCard";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { searchWorks, searchNews } from "@/lib/search";

const tabs = ["全部", "影视作品", "资讯动态"];

export default function Search() {
  const [params] = useSearchParams();
  const keyword = params.get("q") || "";
  const [tab, setTab] = useState("全部");
  const [input, setInput] = useState(keyword);

  useDocumentTitle(
    keyword ? `“${keyword}”的搜索结果 - 杨幂资讯平台` : "搜索 - 杨幂资讯平台",
    `在杨幂明星资讯平台搜索“${keyword}”，同时检索影视作品与资讯动态内容。`,
    "杨幂搜索,杨幂作品搜索,杨幂资讯搜索",
  );

  const workResults = useMemo(() => searchWorks(keyword), [keyword]);
  const newsResults = useMemo(() => searchNews(keyword), [keyword]);

  const showWorks = tab === "全部" || tab === "影视作品";
  const showNews = tab === "全部" || tab === "资讯动态";
  const total = (showWorks ? workResults.length : 0) + (showNews ? newsResults.length : 0);

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-12">
      <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "搜索" }]} />

      <div className="mt-5 max-w-2xl">
        <div className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-full px-5 h-12">
          <i className="ri-search-line text-foreground-500 text-lg" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="搜索作品或资讯关键词"
            className="flex-1 bg-transparent outline-none text-sm md:text-base text-foreground-900 placeholder:text-foreground-400"
          />
          <Link
            to={`/search?q=${encodeURIComponent(input.trim())}`}
            className="px-4 h-8 flex items-center rounded-full bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            搜索
          </Link>
        </div>
      </div>

      <div className="mt-6">
        <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950">
          {keyword ? (
            <>
              “<span className="text-primary-600">{keyword}</span>”的搜索结果
            </>
          ) : (
            "请输入关键词开始搜索"
          )}
        </h1>
        {keyword && <p className="mt-2 text-sm text-foreground-500">共找到 {total} 条相关内容</p>}
      </div>

      <div className="mt-6 flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
              t === tab ? "bg-primary-500 text-background-50 font-semibold" : "text-foreground-600 hover:text-primary-600"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {total > 0 ? (
        <div className="mt-8 space-y-12">
          {showWorks && workResults.length > 0 && (
            <section>
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-5">
                影视作品 <span className="text-sm text-foreground-400 font-label">({workResults.length})</span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {workResults.map((w) => (
                  <WorkCard key={w.id} work={w} />
                ))}
              </div>
            </section>
          )}

          {showNews && newsResults.length > 0 && (
            <section>
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-5">
                资讯动态 <span className="text-sm text-foreground-400 font-label">({newsResults.length})</span>
              </h2>
              <div className="flex flex-col gap-4 md:gap-5">
                {newsResults.map((n) => (
                  <NewsCard key={n.id} item={n} />
                ))}
              </div>
            </section>
          )}
        </div>
      ) : (
        <div className="mt-10 flex flex-col items-center justify-center py-16 text-center">
          <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 mb-4">
            <i className="ri-search-line text-3xl" />
          </span>
          <p className="font-heading text-lg font-semibold text-foreground-800">没有找到相关内容</p>
          <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或从以下入口继续浏览。</p>
          <div className="mt-5 flex items-center gap-3 flex-wrap justify-center">
            <Link
              to="/works"
              className="inline-flex items-center gap-2 px-5 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-film-line" />
              影视作品库
            </Link>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 px-5 h-10 rounded-md bg-secondary-500 text-background-50 text-sm font-label hover:bg-secondary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-newspaper-line" />
              资讯动态
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}